import { FormCard } from '@qbe/ads-blocks-forms';
import { Alert, Box, Spacer, Spinner } from '@qbe/ads-core';
import React, { useCallback, useState } from 'react';

import PartnerBanner from '~/components/PartnerBanner';
import PaymentFrequency from '~/components/PaymentFrequency';
import PaymentMethod from '~/components/PaymentMethod';
import PremiumPanel from '~/components/PremiumPanel';
import { CONFIRMATION_ROUTE } from '~/constants/routes';
import {
  useAppContent,
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
  usePayments,
  useQuote,
} from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { useBreakpoint } from '~/hooks-ds/useBreakpoint';
import { selectIsReferral } from '~/selectors/selectAppDetails';
import { useBindMutation } from '~/services/quotes';
import { payPolicyAction } from '~/slices/payments/thunks';
import {
  qalFormCompleteAction,
  qalFormStartAction,
  qalTrackBuyStartAction,
} from '~/thunks/analytics';
import { handleError } from '~/thunks/error';
import { navigateAction } from '~/thunks/navigateAction';
import { getMonthlyPremium } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { clearPaymentErrors } from '../../slices/payments';

const Payments: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'dsme.payMyBill' });
  const { getTextContent } = useAppContent({ basePath: 'dsme.payments' });

  const isReferral = useAppSelector(selectIsReferral);
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { errors, paymentState } = usePayments();
  const { premiums } = useQuote();
  const [_, { isLoading: isPaymentLoading }] = useBindMutation();
  const [isFormTouched, setFormTouched] = useState(false);

  const oneOffPremium = premiums?.policy?.policyPremium?.transactionPremium?.totalPremium;
  const firstInstalment = getMonthlyPremium(premiums?.policy?.payment?.instalments?.[0]);
  const futureInstalment = getMonthlyPremium(premiums?.policy?.payment?.instalments?.[1]);

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  const handleFormComplete = useCallback(() => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  }, [dispatch]);
  const handleNext = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      e?.preventDefault();
      dispatch(clearPaymentErrors());
      handleFormComplete();
      try {
        const result = await dispatch(payPolicyAction()).unwrap();
        if (result) {
          dispatch(
            navigateAction({
              to: CONFIRMATION_ROUTE,
              navigate,
            })
          );
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    },
    [dispatch, handleFormComplete, navigate]
  );

  const handlePaymentFormEngage = useCallback(() => {
    if (!isFormTouched) {
      dispatch(qalTrackBuyStartAction());
      handleFormStart();
    }
    setFormTouched(true);
  }, [isFormTouched, dispatch, handleFormStart, setFormTouched]);

  const { isMobile } = useBreakpoint();

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <>
      <Box
        sx={
          // TODO this should be equivalent to PREMIUM_PANEL_BREAKPOINT = 1200;
          isMobile
            ? {}
            : {
                position: 'fixed',
                top: '145px',
                marginLeft: '-910px',
                zIndex: 800,
              }
        }
      >
        <PremiumPanel readOnly />
      </Box>
      <FormCard
        variant="sectionTitle"
        title={getTextContent('title')}
        customElement={isReferral ? <PartnerBanner /> : null}
      >
        <PaymentFrequency
          firstInstalment={firstInstalment}
          futureInstalment={futureInstalment}
          oneOffPremium={oneOffPremium}
          onTouch={handlePaymentFormEngage}
        />

        <PaymentMethod onTouch={handlePaymentFormEngage} onSubmitPayment={handleNext} />
        {errors!.length > 0 &&
          errors?.map(({ message }) => (
            <>
              <Spacer size="xs" />
              <Alert key={message} heading={message} variant="error" />
              <Spacer size="xs" />
            </>
          ))}

        <Spinner
          loading={isPaymentLoading || paymentState === 'in-progress'}
          description={getSharedContent('fields.loadingLabel.inProgress')}
        />
      </FormCard>
    </>
  );
};

export default Payments;
